import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { trackCreate, useOpenedGoal } from "@/lib/metrica";
import { rememberSetupCustomer, useSetupReturn } from "@/lib/setupFlow";
import { MOBILE_STICKY_FOOTER } from "@/lib/utils";
import { fmtTRY, CUSTOMER_STATUS_LABEL } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState, StatusBadge, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Eye, Pencil, Pin } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", company_name: "", contact_person: "", phone: "",
  email: "", address: "", tax_office: "", tax_number: "",
  notes: "", status: "active",
};

export default function Customers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/customers");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Dashboard hızlı erişim butonundan gelen ``?new=1`` → mevcut Yeni
  // Müşteri dialog'unu aç ve URL'i temizle (kullanıcı refresh yaparsa
  // tekrar açılmasın).  Formu duplicate etmiyoruz; state'in mevcut
  // ``open`` bayrağı doğrudan tetikleniyor.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      setEditing(null); setForm(EMPTY); setOpen(true);
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return (items ?? []).filter((c) => {
      const matchS = !s || [c.name, c.company_name, c.contact_person, c.phone, c.email]
        .filter(Boolean).some((v) => String(v ?? "").toLowerCase().includes(s));
      const matchSt = statusFilter === "all" || c.status === statusFilter;
      return matchS && matchSt;
    });
  }, [items, search, statusFilter]);

  useOpenedGoal("customer_create_opened", open && !editing);
  useSetupReturn("customer", open);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Müşteri güncellendi");
      } else {
        const { data } = await trackCreate("customer", () => api.post("/customers", form));
        rememberSetupCustomer(data?.id);
        toast.success("Müşteri eklendi");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error("İşlem başarısız");
    }
  };

  const togglePin = async (c) => {
    const next = !c.is_pinned;
    // Optimistic update: liste sırasını backend re-fetch'iyle yeniden
    // uygulayacağız (pinned → en üst).  Hata durumunda revert.
    setItems((prev) => (prev ?? []).map((x) => x.id === c.id ? { ...x, is_pinned: next } : x));
    try {
      await api.patch(`/customers/${c.id}/pin`, { is_pinned: next });
      // Backend'in yeni sıralamasını uygula (is_pinned desc + created_at desc)
      load();
    } catch (_) {
      // Revert
      setItems((prev) => (prev ?? []).map((x) => x.id === c.id ? { ...x, is_pinned: !next } : x));
      toast.error("Sabitleme değiştirilemedi");
    }
  };

  const onEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", company_name: c.company_name || "",
      contact_person: c.contact_person || "", phone: c.phone || "",
      email: c.email || "", address: c.address || "",
      tax_office: c.tax_office || "", tax_number: c.tax_number || "",
      notes: c.notes || "", status: c.status || "active",
    });
    setOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <StickyPageHead>
      <PageHeader
        title="Müşteriler"
        subtitle="Müşterilerinizi ve toplam bakiyelerini yönetin"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg" data-testid="add-customer-button">
                <Plus className="w-4 h-4 mr-1.5" /> Yeni Müşteri
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">
                  {editing ? "Müşteri Düzenle" : "Yeni Müşteri"}
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs text-slate-500 -mt-2" data-testid="customer-form-optional-hint">
                Yalnızca * işaretli alan zorunludur; diğer alanlar opsiyoneldir.
              </p>
              <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customer-form">
                <Field label="Müşteri Adı *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="customer-name-input" />
                <Field label="Firma Adı" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testId="customer-company-input" />
                <Field label="Yetkili Kişi" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} testId="customer-contact-input" />
                <Field label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="customer-phone-input" />
                <Field label="E-posta" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="customer-email-input" />
                <Field label="Vergi Dairesi" value={form.tax_office} onChange={(v) => setForm({ ...form, tax_office: v })} placeholder="Ör: Büyük Mükellefler" testId="customer-tax-office-input" />
                <Field label="Vergi / T.C. Kimlik No" value={form.tax_number} onChange={(v) => setForm({ ...form, tax_number: v })} placeholder="Vergi veya T.C. kimlik numarası" testId="customer-tax-number-input" />
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-700">Durum</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="h-10 rounded-lg" data-testid="customer-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktif</SelectItem>
                      <SelectItem value="passive">Pasif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs text-slate-700">Adres</Label>
                  <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs text-slate-700">Notlar</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                </div>
                <DialogFooter className={`md:col-span-2 ${MOBILE_STICKY_FOOTER}`} data-testid="customer-form-footer">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="customer-save-button">Kaydet</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Müşteri ara..."
            className="pl-9 h-10 rounded-lg border-slate-200"
            data-testid="customer-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 h-10 rounded-lg" data-testid="customer-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Müşteriler</SelectItem>
            <SelectItem value="active">Aktif Müşteriler</SelectItem>
            <SelectItem value="passive">Pasif Müşteriler</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto">{(filtered ?? []).length} kayıt</div>
      </div>
      </StickyPageHead>

      <SectionCard testId="customers-table">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Yükleniyor…</div>
        ) : (filtered ?? []).length === 0 ? (
          <EmptyState title="Müşteri bulunamadı" hint="Yeni Müşteri butonu ile başlayın."
            action={items?.length === 0 && (
              <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}
                      className="h-11 bg-slate-900 hover:bg-slate-800" data-testid="customers-empty-add-button">
                <Plus className="w-4 h-4 mr-1.5" /> İlk Müşterimi Ekle
              </Button>
            )} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Müşteri</th>
                  <th className="px-5 py-3 font-semibold">İletişim</th>
                  <th className="px-5 py-3 font-semibold text-right">Toplam Satış</th>
                  <th className="px-5 py-3 font-semibold text-right">Tahsil Edilen</th>
                  <th className="px-5 py-3 font-semibold text-right">Bekleyen Alacak</th>
                  <th className="px-5 py-3 font-semibold text-center">İş</th>
                  <th className="px-5 py-3 font-semibold">Durum</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(filtered ?? []).map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/60 cursor-pointer transition-colors" data-testid={`customer-row-${c.id}`} onClick={() => navigate(`/customers/${c.id}`)}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); togglePin(c); }}
                          className={`shrink-0 p-1 rounded-md transition-colors ${
                            c.is_pinned
                              ? "text-emerald-600 hover:bg-emerald-50"
                              : "text-slate-300 hover:text-slate-500 hover:bg-slate-100"
                          }`}
                          title={c.is_pinned ? "Sabitlemeyi kaldır" : "Sabitle"}
                          aria-label={c.is_pinned ? "Sabitlemeyi kaldır" : "Sabitle"}
                          data-testid={`customer-pin-${c.id}`}
                        >
                          <Pin
                            className={`w-4 h-4 ${c.is_pinned ? "fill-emerald-600" : ""}`}
                          />
                        </button>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 hover:underline truncate">{c.name}</div>
                          <div className="text-xs text-slate-500 truncate">{c.company_name || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600 text-xs">
                      <div>{c.contact_person || "—"}</div>
                      <div className="text-slate-500">{c.phone || c.email || "—"}</div>
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{fmtTRY(c.total_sales)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-emerald-600">{fmtTRY(c.total_collected)}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">{fmtTRY(c.outstanding)}</td>
                    <td className="px-5 py-3 text-center text-slate-700">{c.job_count}</td>
                    <td className="px-5 py-3">
                      <StatusBadge value={c.status} label={CUSTOMER_STATUS_LABEL[c.status]} />
                    </td>
                    <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => navigate(`/customers/${c.id}`)} data-testid={`customer-view-${c.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(c)} data-testid={`customer-edit-${c.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-700">{label}</Label>
      <Input
        value={value}
        type={type}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="h-10 rounded-lg"
        data-testid={testId}
      />
    </div>
  );
}
